import { ArrowLeft, Home, Building, TrendingUp, Star } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLocations } from "@/hooks/useLocations";

const LocationCard = ({
  image,
  name,
  area,
  count,
  href,
}: {
  image: string;
  name: string;
  area: string;
  count: string;
  href: string;
}) => (
  <a
    href={href}
    className="group relative aspect-[16/10] rounded-xl overflow-hidden block"
  >
    <img
      src={image}
      alt={`${name}, ${area}`}
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
      loading="lazy"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/20 to-transparent" />
    
    {/* Property count badge */}
    <div className="absolute top-4 right-4 bg-accent text-accent-foreground px-3 py-1 rounded-full font-body text-xs font-medium">
      {count} Properties
    </div>

    {/* Location name */}
    <div className="absolute bottom-6 left-6">
      <h3 className="font-display text-2xl md:text-3xl font-light text-primary-foreground">
        {name}
      </h3>
    </div>
  </a>
);

const LocationsPage = () => {
  const { data: locations, isLoading } = useLocations();

  return (
    <div className="bg-brand-canvas min-h-screen">
      <Header />
      <main className="pt-36 md:pt-40 w-full">
        <div className="bg-brand-gradient py-20 md:py-32">
          <div className="px-4 md:px-16 max-w-7xl mx-auto text-center">
            <div className="mb-6">
              <a href="/" className="inline-flex items-center gap-2 text-accent hover:text-accent/80 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                <span className="font-body text-sm">Back to Home</span>
              </a>
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-light text-primary-foreground mb-8">Prime Locations</h1>
            <div className="max-w-3xl mx-auto">
              <p className="font-body text-lg md:text-xl leading-relaxed text-primary-foreground/82">
                Discover Nairobi's most coveted neighborhoods where luxury meets lifestyle.
              </p>
            </div>
          </div>
        </div>

        {/* Location Grid */}
        <div className="px-4 md:px-16 py-16">
          <div className="max-w-7xl mx-auto">
            {isLoading ? (
              <p className="text-center text-primary-foreground/70 font-body">Loading locations...</p>
            ) : locations && locations.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {locations.map((location) => (
                  <LocationCard key={location.id} image={location.image} name={location.name} area={location.area} count={location.property_count} href={`/locations/${location.slug}`} />
                ))}
              </div>
            ) : (
              <p className="text-center text-primary-foreground/70 font-body">No locations available yet.</p>
            )}
          </div>
        </div>

        <div className="bg-brand-surface px-4 md:px-16 py-16">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-display text-3xl md:text-4xl font-light text-primary-foreground mb-4">Why These Locations?</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: TrendingUp, title: "High Growth Potential", desc: "Areas with strong appreciation rates" },
                { icon: Building, title: "Premium Infrastructure", desc: "Excellent roads and modern planning" },
                { icon: Star, title: "Lifestyle Amenities", desc: "World-class shopping and dining" },
                { icon: Home, title: "Quality Living", desc: "Safe, secure environments" },
              ].map((item) => (
                <div key={item.title} className="bg-card/95 border border-border/20 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-6 h-6 text-accent" />
                  </div>
                  <h3 className="font-display text-lg font-light text-foreground mb-2">{item.title}</h3>
                  <p className="font-body text-xs text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LocationsPage;
